import React from 'react';
import { Table, Flex, Input, Button, Modal, Checkbox, Tabs, TreeSelect, Tag, message, Spin, Empty, Space, Tooltip } from 'antd';
import dayjs from 'dayjs';
import usePagination from '../../../components/pagination';

/**
 * 平台组件授权管理（isSysAdmin）
 * 给租户分配平台组件白名单 + 垂直平台类目范围（空=全平台横向）
 */
const COMPONENTS = [
    { value: 'PlatformGoods', label: '平台精选商品' },
    { value: 'PlatformCategory', label: '自动类目' },
    { value: 'CitySelector', label: '城市切换' },
    { value: 'PlatformShop', label: '精选商家' },
];
const COMPONENT_LABEL = {};
COMPONENTS.forEach(c => { COMPONENT_LABEL[c.value] = c.label; });

const ACCOUNT_TYPE_MAP = { 1: '个人', 2: '公司', 3: '个体户' };

/** 租户可读名称：公司名/姓名 优先，兜底 account.title（8位数字标识） */
function tenantName(r) {
  return (r && (r.companyName || r.realname || r.title)) || '-';
}

export default () => {
    const [dataSource, setDataSource] = React.useState([]);
    const [keyword, setKeyword] = React.useState('');
    const [configRow, setConfigRow] = React.useState(null); // 当前配置的租户
    const [configData, setConfigData] = React.useState({ components: [], cateIds: [] });
    const [saving, setSaving] = React.useState(false);
    const [treeData, setTreeData] = React.useState({ NORMAL: [], GOODS: [] });
    const [treeLoading, setTreeLoading] = React.useState(false);
    const [cateActiveKey, setCateActiveKey] = React.useState('NORMAL');
    const { pagination, current, pageSize, setTotal } = usePagination({
        onChange: (page, pageSize) => {
            getList(page, pageSize).then((res) => {
                setDataSource(res.rows);
                setTotal(res.count);
            });
        },
    });

    const getList = async (page = 1, pageSize = 10) => {
        const params = new URLSearchParams({ page, pageSize });
        if (keyword) params.append('keyword', keyword);
        const response = await fetch(`/api/sys/platform/list?${params.toString()}`);
        const result = await response.json();
        if (result && result.success) {
            return result.data;
        }
        return { rows: [], count: 0 };
    };

    React.useEffect(() => {
        getList().then((res) => {
            setDataSource(res.rows);
            setTotal(res.count);
        });
    }, []);

    const refreshList = () => {
        getList(current, pageSize).then((res) => {
            setDataSource(res.rows);
            setTotal(res.count);
        });
    };

    const handleSearch = () => {
        getList(1, pageSize).then((res) => {
            setDataSource(res.rows);
            setTotal(res.count);
        });
    };

    const loadTrees = async () => {
        setTreeLoading(true);
        try {
            const [n, g] = await Promise.all([
                fetch('/api/sys-category/tree?goodsType=NORMAL').then(r => r.json()),
                fetch('/api/sys-category/tree?goodsType=GOODS').then(r => r.json()),
            ]);
            setTreeData({
                NORMAL: Array.isArray(n?.data) ? n.data : [],
                GOODS: Array.isArray(g?.data) ? g.data : [],
            });
        } finally {
            setTreeLoading(false);
        }
    };

    const openConfig = async (row) => {
        setConfigRow(row);
        setConfigData({ components: [], cateIds: [] });
        setCateActiveKey('NORMAL');
        try {
            const response = await fetch(`/api/sys/platform/get?accId=${row.accId}`);
            const result = await response.json();
            if (result?.success) {
                setConfigData({
                    components: result.data.components || [],
                    cateIds: result.data.cateIds || [],
                });
            }
        } catch (e) { /* 保留空配置 */ }
        if (treeData.NORMAL.length === 0 && treeData.GOODS.length === 0) {
            loadTrees();
        }
    };

    const submitConfig = async () => {
        if (!configRow) return;
        setSaving(true);
        try {
            const payload = {
                accId: configRow.accId,
                components: configData.components,
                cateIds: configData.cateIds,
            };
            const response = await fetch('/api/sys/platform/save', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const result = await response.json();
            if (result?.success) {
                message.success('保存成功');
                setConfigRow(null);
                refreshList();
            } else {
                message.error(result?.data || '保存失败');
            }
        } catch (e) {
            message.error('保存失败');
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        {
            title: '账户ID',
            dataIndex: 'accId',
            width: 130,
        },
        {
            title: '租户名称',
            dataIndex: 'title',
            render: (text, r) => {
                const name = tenantName(r);
                return <div>
                    <div>{name}</div>
                    {text && text !== name && <div style={{ fontSize: 12, color: '#999' }}>{text}</div>}
                    {r.realname && r.companyName && r.realname !== r.companyName && <div style={{ fontSize: 12, color: '#999' }}>{r.realname}</div>}
                </div>;
            }
        },
        {
            title: '类型',
            dataIndex: 'type',
            width: 80,
            render: (t) => t ? <Tag>{ACCOUNT_TYPE_MAP[t] || t}</Tag> : '-',
        },
        {
            title: '电话',
            dataIndex: 'phoneNo',
        },
        {
            title: '版本',
            dataIndex: ['version', 'title'],
            render: (text) => text || '-',
        },
        {
            title: '已授权组件',
            dataIndex: 'components',
            render: (list) => {
                if (!list || list.length === 0) return <Tag>未授权</Tag>;
                return <Space wrap>{list.map(c => <Tag color='blue' key={c}>{COMPONENT_LABEL[c] || c}</Tag>)}</Space>;
            }
        },
        {
            title: '类目范围',
            dataIndex: 'cateIds',
            render: (list) => {
                if (!list || list.length === 0) return <Tag>全平台</Tag>;
                return <Tooltip title={`${list.length} 个类目`}><Tag color='orange'>垂直平台 · {list.length} 类目</Tag></Tooltip>;
            }
        },
        {
            title: '创建时间',
            dataIndex: 'createTime',
            render: (text) => text ? dayjs(text).format('YYYY-MM-DD') : '-',
        },
        {
            title: '操作',
            fixed: 'right',
            render: (text, r) => <a onClick={() => openConfig(r)}>配置</a>,
        },
    ];

    return (
        <div>
            <h1>平台组件授权</h1>
            <Flex gap={10} style={{ marginBottom: 12 }} align='center'>
                <Input
                    style={{ width: 300 }}
                    placeholder='搜索租户名/电话/账号/公司'
                    allowClear
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    onPressEnter={handleSearch}
                />
                <Button type='primary' onClick={handleSearch}>查询</Button>
            </Flex>
            <Table rowKey={'accId'} columns={columns} dataSource={dataSource} scroll={{ x: 'max-content' }} pagination={pagination} />

            <Modal
                title={`平台组件配置 - ${tenantName(configRow)}`}
                open={!!configRow}
                onOk={submitConfig}
                confirmLoading={saving}
                onCancel={() => setConfigRow(null)}
                width={680}
                destroyOnClose
            >
                <div style={{ marginBottom: 8 }}>
                    <div style={{ fontWeight: 500, marginBottom: 8 }}>授权组件</div>
                    <Checkbox.Group
                        value={configData.components}
                        onChange={(v) => setConfigData(d => ({ ...d, components: v }))}
                        options={COMPONENTS}
                    />
                </div>
                <div style={{ marginTop: 16 }}>
                    <div style={{ fontWeight: 500, marginBottom: 8 }}>
                        垂直类目范围 <span style={{ fontWeight: 400, color: '#999', fontSize: 12 }}>（不选=全平台横向；选了=只聚合这些类目下的跨租户商品）</span>
                    </div>
                    {treeLoading ? <Spin style={{ display: 'block', margin: '12px auto' }} /> : (
                        <Tabs
                            size="small"
                            activeKey={cateActiveKey}
                            onChange={setCateActiveKey}
                            items={[
                                { key: 'NORMAL', label: '服务类目', children: renderTree('NORMAL') },
                                { key: 'GOODS', label: '实物类目', children: renderTree('GOODS') },
                            ]}
                        />
                    )}
                </div>
            </Modal>
        </div>
    );

    function renderTree(goodsType) {
        const tree = treeData[goodsType] || [];
        if (tree.length === 0) return <Empty description="暂无类目" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
        return (
            <TreeSelect
                style={{ width: '100%' }}
                value={configData.cateIds}
                onChange={(v) => setConfigData(d => ({ ...d, cateIds: v }))}
                allowClear
                multiple
                treeCheckable
                showCheckedStrategy={TreeSelect.SHOW_ALL}
                treeDefaultExpandAll
                maxTagCount="responsive"
                treeData={tree}
                placeholder="请选择类目（不选=全平台）"
                treeNodeFilterProp="label"
            />
        );
    }
};
